import type { VNode } from 'preact';
import { Spinner } from '@core/components/Elements';

import Background from '@assets/images/hero-bg.webp';
import Logo from '@assets/images/cop-logo.webp';

export interface AppLoadingOverlayProps {
  /** Message shown below the spinner. Default: "Loading Experience.." */
  message?: string;
}

const safeAreaInsetTop =
  typeof CSS !== 'undefined' && CSS.supports('padding-top: constant(safe-area-inset-top)')
    ? 'constant(safe-area-inset-top)'
    : 'env(safe-area-inset-top, 0px)';

/**
 * Loading overlay used during AR transition and bootstrap.
 * Kept in main entry chunk so it displays immediately in App Clip.
 */
export function AppLoadingOverlay({ message = 'Loading Experience..' }: AppLoadingOverlayProps): VNode {
  return (
    <div className="fixed inset-0 flex flex-col text-white pointer-events-auto z-50 min-h-[100dvh]">
      {/* Greyscale background with dark overlay */}
      <div
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: `url(${Background})`,
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          filter: 'grayscale(100%)',
        }}
      />
      <div className="absolute inset-0 z-[1] bg-black/0" />

      {/* Header */}
      <header
        className="relative z-10 w-full bg-[#EA2946] px-5 py-5 flex items-center justify-start flex-shrink-0"
        style={{ paddingTop: `calc(${safeAreaInsetTop} + 1rem)` }}
      >
        <img 
          src={Logo} 
          alt="City of Perth" 
          className="w-full h-auto max-w-[140px] sm:max-w-[160px] 2xl:max-w-[210px]"
        />
      </header>

      {/* Content area */}
      <div className="relative z-10 flex-1 flex flex-col px-5 sm:px-8 2xl:px-12">
        {/* Title section - same positioning as HeroSection */}
        <div className="flex-1 w-full flex flex-col items-start justify-center">
          <div className="flex flex-col gap-5 text-left">
            <h1 className="text-white text-5xl sm:text-7xl 2xl:text-8xl font-bold leading-[0.9]">
              Sea Queen and <br />
              Standing Figure
            </h1>
            <h3 className="text-white text-2xl sm:text-3xl 2xl:text-4xl">
              Sculptures by Tony Jones
            </h3>
          </div>
        </div>

        {/* Spinner section - centered at bottom */}
        <div className="w-full flex flex-col items-center pb-16 sm:pb-20 flex-shrink-0">
          <Spinner size="2md" variant="light" />
          <p className="text-white text-xl sm:text-2xl mt-6 tracking-[1px] text-center">
            {message}
          </p>
        </div>
      </div>
    </div>
  );
}
